import FormUtils from './FormUtils.js';

/**
 * Legacy base class: jQuery `get ref()`, `is-initialized`, `is-element-*`.
 * @deprecated use XlibElement.js (docs/element-js-conventions.md) — native DOM,
 *   `data-element-initialized` + `data-element`.
 * @see Consumers 2026-08-06: web-plus-computerwissen, web-reichweiten,
 *   web-sobeo, web-wegweiser-digitale-schule-bedrock, wp-cosi, wp-epaper,
 *   wp-premium, wp-shop-digitalphoto, wp-shop-fmm, wp-stoffwechsel-turbo;
 *   plus SearchResultsPlugin.js — all legacy `is-element-*`. Removable when
 *   this list is empty. The SearchResultsPlugin
 *   coupling is intentional: those projects bootstrap `is-element-*`, so
 *   porting it to XlibElement would break them and free nothing here.
 */
export default class Element {
    constructor(config, ref) {
        this.config = config;
        this.container = ref;

        if (!this.container.getAttribute('is-initialized')) {
            this.setInitialized(true);

            this.init();
            this.initListeners();
        }
    }

    get ref() {
        if (window.$ === undefined || typeof window.$ !== 'function') {
            console.warn('jQuery is not loaded!');
            return [this.container];
        }
        return $(this.container);
    }

    setInitialized(value) {
        this.container.setAttribute('is-initialized', value ? 'true' : null);
    }

    init() {

    }

    initListeners() {

    }

    /**
     * @throws {Error} on a non-2xx response or a failed request
     * @param {Record<string, *>} requestParams
     * @param {{url: string, method: string, params: {only_dispatch: [string, string], render_elements: boolean}}} dispatchConfig from PHP function \XLib\Http\XlibResponse::getDispatchElementConfig()
     * @param {boolean} renderElement
     * @return {Promise<{name: string, data: Object<string,*>, rendered: string, renderedElement: HTMLElement, render_error: string}>}
     */
    async dispatchElement(requestParams, dispatchConfig=null, renderElement=true)
    {
        dispatchConfig = dispatchConfig || this.config.dispatchConfig || this.config.xhrConfig || {};

        if (! dispatchConfig?.url || typeof dispatchConfig?.method !== 'string' || ! dispatchConfig?.params?.only_dispatch?.[0]) {
            throw new Error('please provide a dispatchConfig!');
        }

        const method = dispatchConfig.method.toLowerCase();
        const xlibElementClassPath = dispatchConfig.params.only_dispatch[0];
        const queryParams = method === 'get' ? { ...dispatchConfig.params, ...requestParams } : { ...dispatchConfig.params };
        queryParams.render_elements = renderElement;
        const bodyParams = method === 'post' ? { ...requestParams } : {};

        const query = FormUtils.objectToQueryString(queryParams);
        const requestUrl = query ? `${dispatchConfig.url}?${query}` : dispatchConfig.url;

        this.dispatchAbortController = new AbortController();

        const response = await fetch(requestUrl, {
            method: dispatchConfig.method,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: method === 'post' ? JSON.stringify(bodyParams) : undefined,
            signal: this.dispatchAbortController.signal,
        });

        if (!response.ok) {
            throw new Error(`Element dispatch to ${dispatchConfig.url} failed with status ${response.status}`);
        }

        const dispatchData = await response.json();

        const elementResult = dispatchData?.elements
            ?.find(element => element?.name === xlibElementClassPath);

        if (!elementResult) {
            throw new Error(`Element ${xlibElementClassPath} not found in response.`);
        }

        if (renderElement && elementResult.render_error) {
            console.error(`Render errors for ${elementResult.name}: ${elementResult.render_error}`);
        }

        if (renderElement && typeof elementResult.rendered === 'string') {
            elementResult.renderedElement = document.createElement('div');
            elementResult.renderedElement.innerHTML = elementResult.rendered;
        }

        return elementResult;
    }

    /**
     * Cancels the dispatch this element currently has in flight. The pending `dispatchElement()`
     * promise rejects with an `AbortError`, which the caller is expected to recognise and ignore
     * rather than report as a failure. One request is tracked at a time, so an element that
     * dispatches twice concurrently can cancel only the later one.
     */
    abortDispatch()
    {
        this.dispatchAbortController?.abort();
    }

    /**
     * @deprecated use dispatchElement() — the transport is fetch, not XHR.
     * @param {Record<string, *>} requestParams
     * @param {object} xhrConfig
     * @param {boolean} renderElement
     * @return {Promise<{name: string, data: Object<string,*>, rendered: string, renderedElement: HTMLElement, render_error: string}>}
     */
    async xhrDispatchElement(requestParams, xhrConfig=null, renderElement=true)
    {
        return this.dispatchElement(requestParams, xhrConfig, renderElement);
    }

    /**
     * @param {HTMLElement} renderedElement rendered DOM fragment from {@link Element.dispatchElement()}
     * @param {string} domSelector CSS selector for element in the dom and the response
     * @param {boolean} appendContent true => keep all children and append response children
     * @return {Element}
     */
    renderDomFragment(renderedElement, domSelector, appendContent=false)
    {
        if (!renderedElement?.querySelector) {
            console.warn('no HTML to provided');
            return this;
        }

        const elementReplacement = renderedElement.querySelector(domSelector);
        if (! elementReplacement) {
            console.error(`Element ${domSelector} not found in response.`);
            return this;
        }

        const elementToReplace = this.container.querySelector(domSelector);
        if (! elementToReplace) {
            console.error(`Target element ${domSelector} not found in DOM.`);
            return this;
        }

        if (appendContent) {
            // Add only the new rows, so earlier rows keep their listeners, focus and state; re-parsing all via innerHTML would drop them.
            elementToReplace.insertAdjacentHTML('beforeend', elementReplacement.innerHTML);
        } else {
            elementToReplace.innerHTML = elementReplacement.innerHTML;
        }

        return this;
    }
}
